import { useQuery } from '@tanstack/react-query';
import { LinkBtn } from '../components/LinkBtn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import { companyApi } from '../lib/api.ts';
import { useBranding } from '../lib/branding.tsx';
import type { Company, Question, Submission } from '../lib/types.ts';
import { computeProgress } from '../lib/visibility.ts';

export function CompanyDashboard() {
  const { branding } = useBranding();

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => companyApi.get<{ company: Company }>('/api/me'),
  });
  const submissionsQuery = useQuery({
    queryKey: ['me', 'submissions'],
    queryFn: () => companyApi.get<{ submissions: Submission[] }>('/api/me/submissions'),
  });
  const questionsQuery = useQuery({
    queryKey: ['questions'],
    queryFn: () => companyApi.get<{ questions: Question[] }>('/api/me/questions'),
  });

  if (meQuery.isLoading || submissionsQuery.isLoading || questionsQuery.isLoading) {
    return <CenteredSpinner />;
  }

  const company = meQuery.data?.company;
  const questions = questionsQuery.data?.questions ?? [];
  const submissions = submissionsQuery.data?.submissions ?? [];
  const breakdown = computeProgress(questions, submissions);

  if (!company) return null;

  const status = company.status;
  const message = (() => {
    switch (status) {
      case 'invited':
        return 'Welcome! Get started by completing the questionnaire.';
      case 'in_progress':
        return 'Continue where you left off.';
      case 'review':
        return 'Your submission is under review. We’ll email you once it’s been reviewed.';
      case 'changes':
        return 'Some answers need attention — please review the comments and resubmit.';
      case 'compliant':
        return 'You are compliant. Thank you.';
      case 'expired':
        return 'Your compliance has expired. Please complete the questionnaire again.';
    }
  })();

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          {branding.welcomeHeading ?? 'Welcome'}
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {branding.welcomeMessage ?? message}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-xs uppercase tracking-wide text-slate-500">Progress</div>
          <div className="mt-2"><ProgressBar value={company.progress} /></div>
          <div className="mt-1 text-xs text-slate-500">
            {breakdown.completedRequired} / {breakdown.totalRequired} required complete
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-xs uppercase tracking-wide text-slate-500">Visible questions</div>
          <div className="mt-2 text-2xl font-semibold text-slate-900">{breakdown.visibleIds.size}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="text-xs uppercase tracking-wide text-slate-500">Compliant until</div>
          <div className="mt-2 text-sm text-slate-700">
            {company.expires_at ? new Date(company.expires_at).toLocaleDateString() : '—'}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <p className="text-sm text-slate-700">{message}</p>
        <div className="mt-4 flex gap-2">
          <LinkBtn to="/c/questionnaire">
            {status === 'compliant' ? 'View questionnaire' : 'Open questionnaire'}
          </LinkBtn>
          <LinkBtn variant="secondary" to="/c/documents">View documents</LinkBtn>
        </div>
      </div>
    </div>
  );
}
